import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import type { WebsiteConfig } from "../../types/config";

import { API_URLS } from "./shared";

/**
 * Fetch website configuration from API
 * Returns empty config on error to allow app to continue with defaults
 */
async function fetchWebsiteConfig(signal?: AbortSignal): Promise<WebsiteConfig> {
    const url = `${API_URLS.SOLOMON_DATA}/api/config/website`;
    try {
        const res = await fetch(url, { signal });
        if (!res.ok) {
            // Return empty config on error - allows app to continue with defaults
            return {};
        }
        return (await res.json()) as WebsiteConfig;
    } catch {
        // Return empty config on network error
        return {};
    }
}

/**
 * Hook to fetch website configuration
 *
 * Returns configuration values including:
 * - APYOverride: Manual APY override (decimal format, e.g., 0.127 = 12.7%)
 * - APYCeiling: Maximum APY cap (decimal format, e.g., 0.22 = 22%)
 * - Various date configurations
 *
 * @example
 * ```tsx
 * const { data: config } = useWebsiteConfig();
 * if (config?.APYOverride) {
 *   console.log(`Using override APY: ${Number(config.APYOverride) * 100}%`);
 * }
 * ```
 */
export function useWebsiteConfig(): UseQueryResult<WebsiteConfig> {
    return useQuery<WebsiteConfig>({
        queryKey: ["website-config"],
        queryFn: ({ signal }) => fetchWebsiteConfig(signal),
        staleTime: 10 * 60 * 1000,
        refetchInterval: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: 1
    });
}
